import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Importado para redirecionamento
import './RegisterDesbastePopup.css';
import { API_BASE_URL } from "../../../../config";
import { FaCalendarPlus, FaEdit, FaTimes, FaArrowLeft, FaTrashAlt, FaExclamationTriangle } from 'react-icons/fa';

const initialFulfillmentState = {
    numero: "", data: "", arvores_cortadas: "", lenha: "", toretes: "",
    toras_20_25cm: "", toras_25_33cm: "", toras_acima_33cm: "", preco_lenha: "",
    preco_toretes: "", preco_toras_20_25cm: "", preco_toras_25_33cm: "",
    preco_toras_acima_33cm: "", valor_extracao: "",
};

const RegisterDesbastePopup = ({ isOpen, onClose, imovelId, onUpdateNeeded }) => {
    const [previsoes, setPrevisoes] = useState([]);
    const [previsaoDate, setPrevisaoDate] = useState('');
    const [fulfillmentData, setFulfillmentData] = useState(initialFulfillmentState);
    const [selectedPrevisao, setSelectedPrevisao] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState(null);
    const navigate = useNavigate(); // Hook para navegação

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : "N/A";
    };

    const fetchPrevisoes = useCallback(async () => {
        if (!imovelId) return;
        
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovelId}/desbastes/previsoes`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` // Usa o token JWT
                }
            });

            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    setPrevisoes([]);
                } else {
                    throw new Error("Erro ao carregar previsões pendentes.");
                }
            } else {
                const data = await response.json();
                setPrevisoes(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            setError(err.message);
            setPrevisoes([]);
        } finally {
            setIsLoading(false);
        }
    }, [imovelId, navigate]);

    const resetState = useCallback(() => {
        setPrevisoes([]);
        setPrevisaoDate('');
        setFulfillmentData(initialFulfillmentState);
        setSelectedPrevisao(null);
        setError('');
        setSuccess('');
        setIsLoading(true);
        setShowDeleteConfirmation(false);
        setItemToDeleteId(null);
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchPrevisoes();
        } else {
            setTimeout(resetState, 300);
        }
    }, [isOpen, fetchPrevisoes, resetState]);

    useEffect(() => {
        if (selectedPrevisao) {
            const dataToSet = {};
            for (const key in initialFulfillmentState) {
                dataToSet[key] = selectedPrevisao[key] || '';
            }
            setFulfillmentData(dataToSet);
        }
    }, [selectedPrevisao]);

    const handleFulfillmentChange = (e) => setFulfillmentData({ ...fulfillmentData, [e.target.name]: e.target.value });

    const handlePrevisaoSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }
        if (!previsaoDate) {
            setError("O campo 'previsão' é obrigatório!");
            return;
        }
        setIsSubmitting(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovelId}/desbastes/previsao`, {
                method: "POST",
                headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${token}` }, // Usa o token JWT
                body: JSON.stringify({ previsao: previsaoDate }),
            });

            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Erro ao registrar a previsão.");
            }
            setSuccess("Previsão de desbaste registrada com sucesso!");
            setPrevisaoDate('');
            fetchPrevisoes();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFulfillmentSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }

        if (!fulfillmentData.numero || !fulfillmentData.data || !fulfillmentData.arvores_cortadas) {
            setError("Os campos Número, Data e Árvores Cortadas são obrigatórios!");
            return;
        }
        
        setIsSubmitting(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/desbastes/${selectedPrevisao.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${token}` }, // Usa o token JWT
                body: JSON.stringify(fulfillmentData),
            });

            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Falha ao registrar o desbaste.");
            }

            setSuccess("Desbaste registrado com sucesso na previsão!");
            setSelectedPrevisao(null);
            fetchPrevisoes(); 
            if(onUpdateNeeded) onUpdateNeeded();
            
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDelete = (id) => {
        setItemToDeleteId(id);
        setShowDeleteConfirmation(true);
    };

    const cancelDelete = () => {
        setItemToDeleteId(null);
        setShowDeleteConfirmation(false);
    };

    const executeDelete = async () => {
        if (!itemToDeleteId) return;
        
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }

        setIsSubmitting(true);
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovelId}/desbastes/${itemToDeleteId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` } // Usa o token JWT
            });

            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || "Erro ao excluir a previsão.");
            }
            
            setSuccess("Previsão excluída com sucesso!");
            fetchPrevisoes();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
            cancelDelete();
        }
    };

    if (!isOpen) return null;

    const renderMainView = () => (
        <>
            <form onSubmit={handlePrevisaoSubmit}>
                <section className="register-desbaste-section">
                    <h3 className="register-desbaste-section-title">Adicionar Nova Previsão de Desbaste</h3>
                    <div className="previsao-form-group">
                        <input
                            type="date"
                            name="previsao"
                            value={previsaoDate}
                            onChange={(e) => setPrevisaoDate(e.target.value)}
                            className="form-input"
                        />
                        <button type="submit" className="modal-btn btn-secondary" disabled={isSubmitting}>
                            <FaCalendarPlus /> {isSubmitting ? 'Registrando...' : 'Registrar Previsão'}
                        </button>
                    </div>
                </section>
            </form>
            <div className="register-desbaste-divider"></div>
            <section className="register-desbaste-section">
                <h3 className="register-desbaste-section-title">Previsões Pendentes</h3>
                <div className="previsoes-list-container">
                    {isLoading ? <p>Carregando...</p> :
                     previsoes.length === 0 ? <p className="empty-message">Nenhuma previsão pendente.</p> :
                     (
                         <ul className="previsoes-list">
                             {previsoes.map((prev) => (
                                 <li key={prev.id} className="previsao-item">
                                     <span>{formatDate(prev.previsao)}</span>
                                     <div className="previsao-actions">
                                         <button onClick={() => setSelectedPrevisao(prev)} className="modal-btn btn-success">
                                             <FaEdit /> Registrar
                                         </button>
                                         <button onClick={() => confirmDelete(prev.id)} className="action-btn-previsao-delete" title="Excluir Previsão">
                                             <FaTrashAlt />
                                         </button>
                                     </div>
                                 </li>
                             ))}
                         </ul>
                     )
                    }
                </div>
            </section>
        </>
    );

    const renderFulfillmentView = () => (
        <form onSubmit={handleFulfillmentSubmit}>
            <section className="register-desbaste-section">
                <h3 className="register-desbaste-section-title">
                    Registrando Desbaste para {formatDate(selectedPrevisao.previsao)}
                </h3>
                <div className="form-grid">
                    {Object.keys(initialFulfillmentState).map(key => {
                        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        return (
                            <div key={key} className="input-group">
                                <label htmlFor={key} className="form-label">{label}</label>
                                <input id={key} name={key} type={key === 'data' ? 'date' : 'number'} value={fulfillmentData[key]} onChange={handleFulfillmentChange} className="form-input" step="0.01"/>
                            </div>
                        )
                    })}
                </div>
            </section>
        </form>
    );

    return (
        <>
        {showDeleteConfirmation && (
            <div className="delete-confirmation-overlay" onClick={cancelDelete}>
                <div className="delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
                    <FaExclamationTriangle className="warning-icon" />
                    <h3>Confirmar Exclusão</h3>
                    <p>Tem certeza que deseja excluir esta previsão de desbaste? A ação não pode ser desfeita.</p>
                    <div className="modal-footer-stacked">
                        <button className="btn-danger-stacked" onClick={executeDelete} disabled={isSubmitting}>
                            <FaTrashAlt /> {isSubmitting ? 'Excluindo...' : 'Confirmar Exclusão'}
                        </button>
                        <button className="btn-link-stacked" onClick={cancelDelete} disabled={isSubmitting}>
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        )}
        <div className="register-desbaste-overlay" onClick={onClose}>
            <div className="register-desbaste-content" onClick={(e) => e.stopPropagation()}>
                <header className="register-desbaste-header">
                    <h2 className="register-desbaste-title">Registrar Desbaste</h2>
                    <button className="register-desbaste-close-btn" onClick={onClose}>&times;</button>
                </header>

                <main className="register-desbaste-body">
                    {error && <div className="feedback-message error-message" onClick={() => setError('')}>{error}</div>}
                    {success && <div className="feedback-message success-message" onClick={() => setSuccess('')}>{success}</div>}
                    {selectedPrevisao ? renderFulfillmentView() : renderMainView()}
                </main>
                
                <footer className="register-desbaste-footer">
                    {selectedPrevisao ? (
                        <>
                            <button type="button" className="modal-btn btn-cancel" onClick={() => setSelectedPrevisao(null)}>
                                <FaArrowLeft /> Voltar
                            </button>
                            <button type="button" className="modal-btn btn-success" onClick={handleFulfillmentSubmit} disabled={isSubmitting}>
                                <FaEdit /> {isSubmitting ? 'Salvando...' : 'Salvar Desbaste'}
                            </button>
                        </>
                    ) : (
                        <button type="button" className="modal-btn btn-cancel" onClick={onClose}>
                            <FaTimes /> Fechar
                        </button>
                    )}
                </footer>
            </div>
        </div>
        </>
    );
};

export default RegisterDesbastePopup;